import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { WarningTriangleGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'
import { VStack } from '../vstack'

export type RestrictionCardProps = {
    /** Restriction headline visible to all users (the `aosMessage`). */
    title: string
    /** Translated "APOC" section label. */
    apocLabel?: string
    /** APOC-only message (`message`). Rendered only when provided — the caller gates on role. */
    apocMessage?: string
    /** Translated "Date and time" label. */
    dateTimeLabel: string
    /** Formatted start–end range. */
    dateTimeValue: string
    className?: string
    testID?: string
}

const LabeledBlock: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <VStack className='gap-1'>
        <Text size='caption' tone='muted'>
            {label}
        </Text>
        <Text size='body' tone='default'>
            {value}
        </Text>
    </VStack>
)

/**
 * Stand/gate restriction card (Figma `Stands&GatesMap` / `Stands&GatesTimeline/RestrictionDetails`
 * / `Mobile/Stands&Gates`): a `bg/elevation` card with a `border/strong` hairline. Header is a
 * warning triangle + the restriction title; the body holds an optional APOC block (shown only
 * when `apocMessage` is passed) and the date-and-time range. Labels arrive already translated so
 * the component stays i18n-agnostic and cross-platform (web + mobile).
 */
export const RestrictionCard: React.FC<RestrictionCardProps> = ({
    title,
    apocLabel,
    apocMessage,
    dateTimeLabel,
    dateTimeValue,
    className,
    testID,
}) => (
    <Box
        testID={testID}
        className={twMerge(
            'gap-4 rounded border border-solid border-border-strong bg-aos-bg-elevation p-4',
            className,
        )}
    >
        <HStack className='items-start gap-2'>
            <WarningTriangleGlyph size={20} />
            <Text size='body-lg' weight='semiBold' tone='default' className='min-w-0 flex-1'>
                {title}
            </Text>
        </HStack>
        {apocMessage ? <LabeledBlock label={apocLabel ?? ''} value={apocMessage} /> : null}
        <LabeledBlock label={dateTimeLabel} value={dateTimeValue} />
    </Box>
)
RestrictionCard.displayName = 'RestrictionCard'
